"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowRight, Bot, FileText, Plug, ScrollText, Sparkles } from "lucide-react";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { useDocsI18n, type DocsLocale } from "@/lib/i18n";
import type { ReactNode } from "react";
interface Tool {
  title: Record<DocsLocale, string>;
  description: Record<DocsLocale, string>;
  href: string;
  icon: ReactNode;
}
const tools: Tool[] = [{
  title: {
    en: "llms.txt",
    "zh-CN": "llms.txt"
  },
  description: {
    en: "Machine-readable index of all documentation pages for LLM consumption.",
    "zh-CN": "面向 LLM 消费的机器可读文档页面索引。"
  },
  href: "/llms.txt",
  icon: <FileText className="size-5" />
}, {
  title: {
    en: "llms-full.txt",
    "zh-CN": "llms-full.txt"
  },
  description: {
    en: "Complete documentation content in a single file for full-context LLM ingestion.",
    "zh-CN": "把完整文档内容汇总成单个文件，便于 LLM 全上下文读取。"
  },
  href: "/llms-full.txt",
  icon: <ScrollText className="size-5" />
}, {
  title: {
    en: "MCP Server",
    "zh-CN": "MCP Server"
  },
  description: {
    en: "Use doteb as an MCP server for Claude Code, Cursor, and other MCP-compatible clients.",
    "zh-CN": "将 doteb 作为 MCP server，用于 Claude Code、Cursor 和其他兼容 MCP 的客户端。"
  },
  href: "/guides/mcp",
  icon: <Plug className="size-5" />
}, {
  title: {
    en: "Agent Skills",
    "zh-CN": "Agent Skills"
  },
  description: {
    en: "Packaged instructions and guidelines for AI coding agents to generate higher-quality code.",
    "zh-CN": "面向 AI coding agents 的打包说明与规范，用于生成更高质量的代码。"
  },
  href: "/guides/agent-skills",
  icon: <Sparkles className="size-5" />
}, {
  title: {
    en: "Templates & Agents",
    "zh-CN": "模板与 Agent"
  },
  description: {
    en: "Pre-built templates and agent configurations to get started quickly.",
    "zh-CN": "预置模板和 agent 配置，帮助你快速开始。"
  },
  href: "https://www.bacuo.com/templates",
  icon: <Bot className="size-5" />
}];
export function AIToolingCards() {
  const $ = _c(5);
  const posthog = usePostHog();
  const {
    locale
  } = useDocsI18n();
  let t0;
  if ($[0] !== locale || $[1] !== posthog) {
    t0 = tools.map(tool => <Link key={tool.href} href={tool.href} onClick={() => {
      posthog.capture("docs_ai_tooling_card_click", {
        tool: tool.title.en,
        href: tool.href
      });
    }} className="group relative flex flex-col gap-3 rounded-xl border border-fd-border bg-fd-card p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-fd-primary/40"><div className="flex items-center justify-between"><div className="flex items-center gap-3"><div className="rounded-lg bg-fd-primary/10 p-2 text-fd-primary transition-colors duration-200 group-hover:bg-fd-primary/20">{tool.icon}</div><h3 className="text-sm font-semibold tracking-tight text-fd-foreground">{tool.title[locale]}</h3></div><ArrowRight className="size-4 -translate-x-1 text-fd-muted-foreground opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100 group-hover:text-fd-primary" /></div><p className="text-[13px] leading-relaxed text-fd-muted-foreground">{tool.description[locale]}</p></Link>);
    $[0] = locale;
    $[1] = posthog;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== t0) {
    t1 = <div className="not-prose grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{t0}</div>;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}